import { computed, inject, Signal } from '@angular/core';
import { ProductsService } from './products.service';
import { isOrderState, OrderState } from 'app/order/models';
import { Product } from '@models/product';
import { Tag } from '@models/tag';

export function orderProductHasTag(tag: Tag): Signal<boolean> {
    const product = inject(ProductsService).product;
    return computed(() => hasTag(product(), tag));
}

/**
 * @description checks if a given {@link OrderState} has the tag
 * defined by `key` with the value `value`. If `value` is omitted,
 * `false`, or `null` it will also return `true` when no tag for
 * the given `key` exists. Otherwise it will check if the tag's
 * value is strictly equal to `value` and return the result.
 * @param {OrderState} state the `OrderState` to check.
 * @param {string} key the key or name of the tag to check.
 * @param {any?} value the value the tag should have.
 * @returns {boolean} `true` when a tag with the given `key` and `value`
 * exists on the `OrderState`, `false` if not.
 */
export function hasTag(state: OrderState, key: string, value?: boolean): boolean;

/**
 * @description checks if a given {@link Product} has the tag
 * defined by `key`. Tags on `Product`s have no value, so the
 * tag can either be there or not.
 * @param {Product} product the product to check.
 * @param {string} key the key or name of the tag to check.
 * @returns {boolean} `true` is the tag for `key` exists on the
 * given `Product`, `false` if not.
 */
export function hasTag(product: Product, key: Tag): boolean;
export function hasTag(stateOrProduct: OrderState | Product, key: Tag, value?: boolean): boolean {
    if (isOrderState(stateOrProduct)) {
        return value === false || value == null
            ? !stateOrProduct.tags.get(key)
            : stateOrProduct.tags.get(key) === value;
    } else {
        return stateOrProduct.tags.includes(key);
    }
}

export const hasTags = (state: OrderState, tags: Partial<Record<Tag, boolean>>): boolean =>
    Object.entries(tags).every(([key, value]) => hasTag(state, key, value));

export const getTagsFromProduct = (product: Product): OrderState['tags'] => {
    const orderStateTags = new Map();
    if (product && product.tags) {
        for (const tag of product.tags) {
            orderStateTags.set(tag, true);
        }
    }
    return orderStateTags;
};
